import { Identifier, Timestamp } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Appointment } from '../appointment/_types';

interface FetchCalendarOptions {
  start: number;
  end: number;
  timeZone?: string;
  cancelled?: boolean;
  noAvailabilitySchedules?: boolean;
  location?: Identifier | string; // Can be comma-separated for multiple locations
  userId?: Identifier | string; // Can be comma-separated for multiple trainers
  type: 'appointments' | 'schedules';
  spotsRemaining?: number;
  serviceId?: Identifier | string; // Can be comma-separated for multiple services
  category?: Identifier | string; // Can be comma-separated for multiple categories
  resourceId?: Identifier | string; // Can be comma-separated for multiple booked assets
  trainerTag?: string; // Staff member tags
  currentUserId?: Identifier; // Current user ID for fallback
}

export interface CalendarAppointment {
  data: Appointment;
  date: Timestamp;
  startTime: string;
  endTime: string;
  image: string | null;
  text: string;
  objectId: Identifier;
  objectType: 'appointment';
  primaryTrainerIds: Identifier[];
  secondaryTrainerIds: Identifier[];
}

type FetchCalendarResponse = any[];

export const buildCalendarUserQueryParams = (
  userId?: Identifier | string
): {
  platform?: boolean;
  user_id?: Identifier | string;
  user_ids?: string;
} => {
  const userIdStr = userId ? String(userId) : '';

  if (!userIdStr) {
    return { platform: true };
  }

  if (userIdStr.includes(',')) {
    return { user_ids: userIdStr };
  }

  return { user_id: userId };
};

export const fetchCalendar = async ({
  type,
  start,
  end,
  timeZone,
  cancelled,
  location,
  noAvailabilitySchedules,
  userId,
  spotsRemaining,
  serviceId,
  category,
  resourceId,
  trainerTag,
}: FetchCalendarOptions): Promise<FetchCalendarResponse> => {
  const { platform, user_id, user_ids } = buildCalendarUserQueryParams(userId);

  const params = {
    platform,
    object_type: type,
    start,
    end,
    time_zone: timeZone,
    user_id,
    user_ids,
    'q[cancelled]': cancelled,
    'q[location]': location,
    'q[noAvailabilitySchedules]': noAvailabilitySchedules,
    'q[spots_remaining]': spotsRemaining,
    'q[service]': serviceId,
    'q[service_tag]': category,
    'q[resource]': resourceId,
    'q[trainer_tag]': trainerTag,
  };
  const response = await api.get('/api/v4/calendar', {
    params,
  });

  const data = camelcaseKeys(response.data, { deep: true });

  return data;
};
